import { useMemo } from 'react'
import { useStatuses } from './useWorkspaceStatuses'
import type {TaskFilter} from '@/lib/types/filter';
import type {FilterContext} from '@/lib/filters/applyFilters';
import { useCycleWorkItems, useTaskLabels, useTasks } from '@/lib/db/rxdb/hooks'
import useHideCompletedStore from '@/store/HideCompletedStore'
import {  taskPassesFilters } from '@/lib/filters/applyFilters'
import { EMPTY_FILTERS  } from '@/lib/types/filter'
import { useCustomFieldValuesForTasks } from '@/components/features/custom-fields/useCustomFieldsForTask'

/**
 * Hook that returns tasks filtered by projectId
 * Returns both top-level tasks and sub-tasks for the project
 */
export function useProjectTasks(
  projectId: string | null | undefined,
  filters: Array<TaskFilter> = EMPTY_FILTERS,
  options?: { includeCompleted?: boolean },
) {
  const { tasks: allTasks, isLoading: tasksLoading } = useTasks({
    isDeleted: false,
  })

  const {
    open: { statusIds: openStatusIds },
    isLoading: statusesLoading,
  } = useStatuses()

  // The board has a Done column, so it opts out of the hide-completed filter.
  const includeCompleted = options?.includeCompleted ?? false
  const hideCompleted = useHideCompletedStore((s) => s.hideCompleted) && !includeCompleted

  const hasLabelFilter = filters.some((f) => f.fieldKey === 'labels' && f.values.length > 0)
  const { taskLabels } = useTaskLabels()

  // Build a taskId -> Set<labelId> lookup for label filtering
  const taskLabelMap = useMemo(() => {
    if (!hasLabelFilter) return null
    const map = new Map<string, Set<string>>()
    for (const tl of taskLabels) {
      const existing = map.get(tl.taskId)
      if (existing) {
        existing.add(tl.labelId)
      } else {
        map.set(tl.taskId, new Set([tl.labelId]))
      }
    }
    return map
  }, [hasLabelFilter, taskLabels])

  const hasCycleFilter = filters.some((f) => f.fieldKey === 'cycle' && f.values.length > 0)
  const { cycleWorkItems } = useCycleWorkItems()

  // Build a taskId -> Set<cycleId> lookup for cycle filtering
  const taskCycleMap = useMemo(() => {
    if (!hasCycleFilter) return null
    const map = new Map<string, Set<string>>()
    for (const cw of cycleWorkItems) {
      if (cw.isDeleted) continue
      const existing = map.get(cw.workItemId)
      if (existing) {
        existing.add(cw.cycleId)
      } else {
        map.set(cw.workItemId, new Set([cw.cycleId]))
      }
    }
    return map
  }, [hasCycleFilter, cycleWorkItems])

  // Custom field value lookup — only loads when a cf_ filter is active
  const hasCustomFieldFilter = filters.some(
    (f) => f.fieldKey.startsWith('cf_') && f.values.length > 0,
  )

  const taskIdsForCF = useMemo(() => {
    if (!hasCustomFieldFilter || !allTasks.length || !projectId) return []
    return allTasks
      .filter((t) => t.projectId === projectId && !t.isArchived)
      .map((t) => t.id)
  }, [hasCustomFieldFilter, allTasks, projectId])

  const { batchValueMap: customFieldValueMap } = useCustomFieldValuesForTasks(
    hasCustomFieldFilter ? taskIdsForCF : [],
  )

  const filterContext = useMemo<FilterContext>(
    () => ({
      taskLabelMap,
      taskCycleMap,
      customFieldValueMap: hasCustomFieldFilter ? customFieldValueMap : null,
    }),
    [taskLabelMap, taskCycleMap, hasCustomFieldFilter, customFieldValueMap],
  )

  const hasFilters = filters.length > 0

  const tasks = useMemo(() => {
    if (!projectId || !allTasks.length) {
      return []
    }

    return allTasks.filter((task) => {
      if (task.projectId !== projectId) return false
      if (task.isArchived) return false
      if (hideCompleted && !openStatusIds.includes(task.statusId)) return false
      if (hasFilters && !taskPassesFilters(task, filters, filterContext))
        return false
      return true
    })
  }, [allTasks, projectId, hideCompleted, openStatusIds, hasFilters, filters, filterContext])

  const topLevelTasks = useMemo(() => {
    return tasks.filter((task) => !task.parentId)
  }, [tasks])

  // The same scope before user filters, so an empty result can explain which
  // filter emptied it (design 9c). Only computed when filters are active.
  const unfilteredTopLevelTasks = useMemo(() => {
    if (!hasFilters || !projectId) return topLevelTasks
    return allTasks.filter(
      (task) =>
        task.projectId === projectId &&
        !task.isArchived &&
        !task.parentId &&
        (!hideCompleted || openStatusIds.includes(task.statusId)),
    )
  }, [hasFilters, projectId, topLevelTasks, allTasks, hideCompleted, openStatusIds])

  const subTasks = useMemo(() => {
    return tasks.filter((task) => task.parentId)
  }, [tasks])

  return {
    tasks,
    topLevelTasks,
    unfilteredTopLevelTasks,
    filterContext,
    subTasks,
    isLoading: tasksLoading || statusesLoading,
  }
}
